import { useCallback } from 'react';

import {
  Field,
  FieldProps,
  FieldInputProps,
  FieldRenderProps,
} from 'react-final-form';

import { ChipsInput as Input, ChipsInputItem } from '@vk-hr-tek/ui/input';

/**
 * Компонент ввода чипсов (ChipsInput) с интеграцией в react-final-form.
 * Поддерживает различные типы данных, валидацию, подсказки и другие функции.
 *
 * @public
 * @param type - Тип данных, ожидаемых на входе.
 * @param label - Метка для поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param name - Уникальное имя поля для react-final-form.
 * @param onChange - Колбэк-функция на изменение значения.
 * @param items - Массив элементов для чипсов.
 * @param required - Флаг обязательности поля.
 * @param disabled - Флаг отключения поля.
 * @param size - Размер поля.
 * @param showError - Флаг отображения ошибок.
 * @param loading - Флаг загрузки данных.
 * @param tooltip - Текст подсказки.
 * @param testId - Идентификатор для тестирования.
 * @returns Компонент ввода чипсов.
 */

export const ChipsInput = ({
  type,
  label,
  labelIcon,
  name,
  onChange,
  items,
  required,
  disabled = false,
  size = 'large',
  showError = true,
  loading = false,
  tooltip,
  testId,
  ...rest
}: {
  type?: 'number' | 'string';
  items: ChipsInputItem[];
} & FieldProps<
  string | number | undefined,
  FieldRenderProps<string | number | undefined>
>) => {
  const handleChange = useCallback(
    (input: FieldInputProps<string | number | undefined, HTMLElement>) =>
      (nextValue: string | number | undefined) => {
        const convertedVal =
          type === 'number' && typeof nextValue === 'string'
            ? parseInt(nextValue, 10)
            : nextValue;

        input.onChange(convertedVal);

        if (onChange) {
          onChange(convertedVal);
        }
      },
    [type, onChange],
  );

  return (
    <Field name={name} {...rest} disabled={disabled}>
      {({ input, meta }) => {
        return (
          <Input
            {...input}
            testId={testId}
            items={items}
            tooltip={tooltip}
            value={input.value}
            onChange={handleChange(input)}
            size={size}
            required={required}
            error={meta.touched && !!meta.error}
            helperText={showError && meta.touched && meta.error}
            label={label}
            labelIcon={labelIcon}
            loading={loading}
            disabled={disabled}
            activeSingleOption={false}
          />
        );
      }}
    </Field>
  );
};
